import type { CommissionPayableServiceStatus } from "@billiard/contracts";
import { commissionPayableModeTerm, syncStateTerm } from "@billiard/contracts";
import { useQuery } from "@tanstack/react-query";
import { Link } from "react-router-dom";
import type { OperatorIdentity } from "./api";
import {
  commissionPayableBusinessMetricDetailQueryKey,
  getCommissionPayableBusinessMetricDetail,
} from "./businessMetricDictionary";

export type CommissionPayableBusinessMetricDetailRoute =
  | { kind: "NONE" }
  | { kind: "INVALID" }
  | { kind: "DETAIL"; businessDate: string };

type CommissionPayableDetailLoader = typeof getCommissionPayableBusinessMetricDetail;

type CommissionPayableBusinessMetricDetailPanelProps = {
  operator: OperatorIdentity;
  effectiveRoles: readonly OperatorIdentity["role"][];
  route: Exclude<CommissionPayableBusinessMetricDetailRoute, { kind: "NONE" }>;
  loader?: CommissionPayableDetailLoader;
};

const serviceStatusLabels: Record<CommissionPayableServiceStatus, string> = {
  COMPLETED: "正常完成",
  EARLY_ENDED: "提前结束",
  ABNORMAL_TERMINATED: "异常终止",
  REVERSED: "已冲正",
};

function isCanonicalBusinessDate(value: string): boolean {
  if (!/^\d{4}-\d{2}-\d{2}$/u.test(value)) return false;
  const milliseconds = Date.parse(`${value}T00:00:00.000Z`);
  return (
    Number.isFinite(milliseconds) && new Date(milliseconds).toISOString().slice(0, 10) === value
  );
}

export function commissionPayableBusinessMetricDetailRouteFromSearch(
  search: string,
): CommissionPayableBusinessMetricDetailRoute {
  const params = new URLSearchParams(search);
  const views = params.getAll("view");
  if (views.length === 0) return { kind: "NONE" };

  const businessDates = params.getAll("businessDate");
  if (
    [...params.entries()].length !== 2 ||
    views.length !== 1 ||
    views[0] !== "COMMISSION_PAYABLE_DETAILS" ||
    businessDates.length !== 1
  ) {
    return { kind: "INVALID" };
  }
  const businessDate = businessDates[0] ?? "";
  return isCanonicalBusinessDate(businessDate)
    ? { kind: "DETAIL", businessDate }
    : { kind: "INVALID" };
}

function formatMoney(fen: number): string {
  return `¥${(Math.abs(fen) / 100).toFixed(2)}`;
}

function formatSignedMoney(fen: number): string {
  if (fen > 0) return `+${formatMoney(fen)}`;
  if (fen < 0) return `-${formatMoney(fen)}`;
  return formatMoney(0);
}

function formatRate(rateBps: number): string {
  return `${(rateBps / 100).toFixed(2)}%`;
}

function formatTime(timestamp: string, timeZone: string): string {
  return new Intl.DateTimeFormat("zh-CN", {
    dateStyle: "medium",
    timeStyle: "medium",
    hour12: false,
    timeZone,
  }).format(new Date(timestamp));
}

function ruleParameterLabel(item: {
  commissionMode: "PERCENTAGE" | "FIXED" | "MONTHLY_TIERED";
  commissionRateBps: number;
  fixedCommissionFen: number;
  tierCalculationMode: "SEGMENTED_PROGRESSIVE" | "WHOLE_MONTH" | null;
}): string {
  if (item.commissionMode === "PERCENTAGE") {
    return `冻结提成比例 ${formatRate(item.commissionRateBps)}`;
  }
  if (item.commissionMode === "FIXED") {
    return `每笔固定提成 ${formatMoney(item.fixedCommissionFen)}`;
  }
  const tierMode = item.tierCalculationMode === "WHOLE_MONTH" ? "达档后整月统一" : "每一档分别累计";
  return `${tierMode}；按冻结规则版本的月度阶梯累计计算，本行金额为服务结束时的权威差额`;
}

function defaultReason(status: CommissionPayableServiceStatus): string {
  return status === "REVERSED"
    ? "原服务被退款或纠正，本次扣回对应预计提成。"
    : "服务结束后，按当时冻结的规则形成预计提成。";
}

function AuthorizedCommissionPayableBusinessMetricDetail({
  operator,
  route,
  loader,
}: {
  operator: OperatorIdentity;
  route: Extract<CommissionPayableBusinessMetricDetailRoute, { kind: "DETAIL" }>;
  loader: CommissionPayableDetailLoader;
}): React.JSX.Element {
  const context = {
    tenantId: operator.tenantId,
    storeId: operator.storeId,
    operatorId: operator.id,
  };
  const detail = useQuery({
    queryKey: commissionPayableBusinessMetricDetailQueryKey(context, route.businessDate),
    queryFn: ({ signal }) => loader(route.businessDate, context, signal),
    refetchOnMount: "always",
    retry: false,
  });
  const responseMatchesSelection =
    detail.data?.tenantId === operator.tenantId &&
    detail.data.storeId === operator.storeId &&
    detail.data.businessDate === route.businessDate;
  const visibleDetail =
    !detail.isFetching && !detail.isError && responseMatchesSelection ? detail.data : undefined;
  const responseUnsafe =
    !detail.isFetching && detail.data !== undefined && !responseMatchesSelection;

  return (
    <main className="commercial-desk" aria-label="营业日预计提成应付完整明细">
      <header className="commerce-header">
        <div>
          <Link className="button secondary compact" to="/orders">
            返回订单页
          </Link>
          <small>{route.businessDate} · 完整终态助教服务账本</small>
          <h1>营业日预计提成明细</h1>
          <p>
            这里是服务结束后按冻结规则形成的预计提成，不等于工资已经发放。冲正会单独显示为负数；某一条记录待复核，只影响该条，不表示整套
            ERP 离线。
          </p>
        </div>
      </header>

      {detail.isFetching ? (
        <output className="empty-state" aria-live="polite">
          正在核对 {route.businessDate}{" "}
          的完整预计提成账本，确认前不会显示旧日期、旧账号或旧门店的金额。
        </output>
      ) : detail.isError || responseUnsafe ? (
        <div className="error-band" role="alert">
          <span>
            这项预计提成明细暂时无法安全读取，旧明细和金额已隐藏；只影响本项查看，不表示整套 ERP
            离线。
          </span>
          <button
            className="button secondary compact"
            type="button"
            onClick={() => detail.refetch()}
          >
            重新读取本项提成
          </button>
        </div>
      ) : visibleDetail ? (
        <>
          <section className="summary-grid" aria-label="营业日预计提成汇总">
            <div>
              <span>净预计提成</span>
              <strong>{formatSignedMoney(visibleDetail.valueFen)}</strong>
            </div>
            <div>
              <span>正向提成笔数</span>
              <strong>{visibleDetail.earningFactCount} 笔</strong>
            </div>
            <div>
              <span>冲正笔数</span>
              <strong>{visibleDetail.reversalFactCount} 笔</strong>
            </div>
          </section>

          {visibleDetail.items.length === 0 ? (
            <section className="empty-state" aria-label="营业日预计提成为空">
              <strong>该营业日没有终态助教服务提成记录</strong>
              <p>
                完整服务账本已核对；这里只表示本项没有预计提成，不代表工资已结清或其他经营数据为零。
              </p>
            </section>
          ) : (
            <section aria-label="营业日逐笔预计提成">
              <div className="commerce-section-title">
                <div>
                  <h2>逐笔预计提成与冲正</h2>
                  <p>以下金额、规则版本和关键参数均来自服务结算时冻结的权威记录。</p>
                </div>
              </div>
              <div className="member-lifecycle-timeline-list">
                {visibleDetail.items.map((item) => {
                  const commissionMode = commissionPayableModeTerm(item.commissionMode);
                  const sync = syncStateTerm(item.syncState);
                  return (
                    <article key={item.payableRecordNumber}>
                      <div>
                        <strong>提成明细号 {item.payableRecordNumber}</strong>
                        <time dateTime={item.occurredAt}>
                          {formatTime(item.occurredAt, visibleDetail.timeZone)}
                        </time>
                      </div>
                      <dl>
                        <div>
                          <dt>原订单</dt>
                          <dd>
                            <Link
                              className="member-lifecycle-order-link"
                              to={`/orders?orderQuery=${encodeURIComponent(item.orderNumber)}`}
                            >
                              {item.orderNumber}
                            </Link>
                          </dd>
                        </div>
                        <div>
                          <dt>原订单营业日</dt>
                          <dd>{item.orderBusinessDate}</dd>
                        </div>
                        <div>
                          <dt>实际服务日 / 时间</dt>
                          <dd>
                            {item.serviceBusinessDate} /{` `}
                            <time dateTime={item.occurredAt}>
                              {formatTime(item.occurredAt, visibleDetail.timeZone)}
                            </time>
                          </dd>
                        </div>
                        <div>
                          <dt>助教</dt>
                          <dd>
                            {item.coachNumber} · {item.coachDisplayName}
                          </dd>
                        </div>
                        <div>
                          <dt>桌号</dt>
                          <dd>{item.tableNumber}</dd>
                        </div>
                        <div>
                          <dt>服务项目</dt>
                          <dd>
                            {item.serviceTypeCode} · {item.serviceTypeName}
                          </dd>
                        </div>
                        <div>
                          <dt>轮钟 / 点钟</dt>
                          <dd>{item.assignmentType === "ROTATION" ? "轮钟" : "顾客点钟"}</dd>
                        </div>
                        <div>
                          <dt>服务状态</dt>
                          <dd>{serviceStatusLabels[item.serviceStatus]}</dd>
                        </div>
                        <div>
                          <dt>计费时长</dt>
                          <dd>
                            {item.billedMinutes} 分钟
                            {item.serviceStatus === "REVERSED"
                              ? "（冲正记录不新增计费时长）"
                              : null}
                          </dd>
                        </div>
                        <div>
                          <dt>服务费</dt>
                          <dd>{formatSignedMoney(item.serviceFeeFen)}</dd>
                        </div>
                        <div>
                          <dt>提成基数</dt>
                          <dd>{formatSignedMoney(item.commissionBaseFen)}</dd>
                        </div>
                        <div>
                          <dt>本笔预计提成</dt>
                          <dd>{formatSignedMoney(item.commissionFen)}</dd>
                        </div>
                        <div>
                          <dt>冻结规则</dt>
                          <dd>
                            <strong>
                              {commissionMode.label} · 第 {item.ruleVersion} 版
                            </strong>
                            <p>{ruleParameterLabel(item)}</p>
                            <p>{commissionMode.explanation}</p>
                          </dd>
                        </div>
                        <div>
                          <dt>形成原因</dt>
                          <dd>{item.reason ?? defaultReason(item.serviceStatus)}</dd>
                        </div>
                        <div>
                          <dt>离线来源</dt>
                          <dd>{item.offlineOrigin ? "离线期间产生" : "联网期间产生"}</dd>
                        </div>
                        <div>
                          <dt>同步状态</dt>
                          <dd>
                            <strong>{sync.label}</strong>
                            <p>{sync.explanation}</p>
                            {item.syncState !== "SYNCED" ? (
                              <p>本条同步状态需要关注，只影响这一条，不表示整套 ERP 离线。</p>
                            ) : null}
                          </dd>
                        </div>
                      </dl>
                    </article>
                  );
                })}
              </div>
            </section>
          )}
        </>
      ) : null}
    </main>
  );
}

export function CommissionPayableBusinessMetricDetailPanel({
  operator,
  effectiveRoles,
  route,
  loader = getCommissionPayableBusinessMetricDetail,
}: CommissionPayableBusinessMetricDetailPanelProps): React.JSX.Element {
  if (route.kind === "INVALID") {
    return (
      <main className="commercial-desk" aria-label="预计提成明细链接无效">
        <div className="error-band" role="alert">
          <span>预计提成明细的营业日期无法安全确认，系统没有读取或展示普通订单列表。</span>
          <Link className="button secondary compact" to="/orders">
            返回订单页
          </Link>
        </div>
      </main>
    );
  }

  const authorized = effectiveRoles.some((role) => role === "OWNER" || role === "MANAGER");
  if (!authorized) {
    return (
      <main className="commercial-desk" aria-label="预计提成明细无查看权限">
        <div className="error-band" role="alert">
          <span>当前账号不能查看全店预计提成明细，系统没有读取普通订单或提成金额。</span>
          <Link className="button secondary compact" to="/orders">
            返回订单页
          </Link>
        </div>
      </main>
    );
  }

  return (
    <AuthorizedCommissionPayableBusinessMetricDetail
      key={`${operator.tenantId}\u0000${operator.storeId}\u0000${operator.id}\u0000${route.businessDate}`}
      operator={operator}
      route={route}
      loader={loader}
    />
  );
}
